import * as React from "react";
import { toast } from "react-toastify";

import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { PageProps } from "src/pages/Router/routes";
import {
  useDropsApi,
  IDrop
} from "src/core/drops/api/drops.api";
import {
  FilterParams,
  PaginatedParams
} from "src/library/utils";
import Table from "src/core/dashboard/components/widgets/table/Table";
import { getDropTableColumns } from "src/core/shared/components/tables/dropsTableColumns";
import {
  ActionButton,
  Button
} from "src/library/components/Button";
import { ITableInstance } from "src/library/components/Tables/Table";
import { LikeToDo } from "src/library/components/LikeToDo";
import { VanSalesDropsModal } from "src/core/drops/components/VanSalesDropsModal";

const defaultPagination: PaginatedParams<IDrop> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "name", "ASC" ] ]
};

/**
 * Main Van sales page for admins
 */
const VanSalesDropsPage: React.ReactFCWithChildren<PageProps<unknown>> = ({ title, ...props }) => {
  const { history, terminology } = props;
  const [ getVanSalesDropsRes, getVanSalesDropsReq ] = useDropsApi("LIST_DROPS");
  const [ updateDropRes, updateDrop ] = useDropsApi("UPDATE_DROP");
  const [ showModal, setShowModal ] = React.useState(false);
  const TableRef = React.useRef<ITableInstance<IDrop>>(null);
  const dropsData = getVanSalesDropsRes.data;

  const onFetchData = React.useCallback(
    (params: {filters: FilterParams<IDrop>, pagination: PaginatedParams<IDrop>}) => {
      getVanSalesDropsReq({
        pagination: params.pagination,
        filters: {
          ...params.filters,
          vanSalesOrdering: true
        }
      });
    },
    [ getVanSalesDropsReq ]
  );

  const columns = React.useMemo(() => {
    return getDropTableColumns({
      actionButton: info => {
        return (
          <ActionButton
            key={`action-${info.row.original.id}`}
            title="Actions"
            items={[
              {
                title: "Remove",
                action: () => {
                  updateDrop({
                    id: info.row.original.id,
                    vanSalesOrdering: false
                  });
                }
              }
            ]}
            loading={getVanSalesDropsRes.pending}
          />
        );
      },
      terminology

    });
  }, [ terminology, updateDrop ]);

  React.useEffect(() => {
    if (updateDropRes.data) {
      TableRef.current?.reloadTable();
    } else if (updateDropRes.error) {
      toast.error(updateDropRes.error.message);
    }
  }, [ updateDropRes.data, updateDropRes.error ]);

  return (
    <Layout
      title={title}
      {...props}
    >
      <MainContentWrapper fill>
        <LikeToDo
          copy="What would you like to do?"
          buttons={[
            <>
              <Button onClick={() => setShowModal(true)}>
                Add drops to van sales
              </Button>
            </>
          ]}
        />

        <Table
          ref={TableRef}
          error={getVanSalesDropsRes.error}
          tableOf={terminology.drop.plural.text}
          loading={getVanSalesDropsRes.pending}
          data={dropsData ? dropsData.result : []}
          onFetchData={onFetchData}
          rowCount={dropsData ? dropsData.count : 0}
          pageCount={dropsData ? dropsData.pages : 0}
          defaultPagination={defaultPagination}
          mainAction={row => history.push(`/admin/customers/${row.original.customerId}/drops/${row.original.id}/orders`)}
          columns={columns}
        />

        <VanSalesDropsModal
          open={showModal}
          onDismiss={() => {
            setShowModal(false);
            TableRef.current?.reloadTable();
          }}
        />

      </MainContentWrapper>
    </Layout>
  );
};

export default VanSalesDropsPage;
